import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import {
  fetchAccountsSilent,
  setAccountEditing,
  useAdminSettingsAccounts,
  useAdminSettingsEditAccountResult,
  useAdminSettingsEditAccountGroupResult,
} from '../adminSettingsSlice';
import { ContentsHeader } from 'components/ContentsHeader';
import msgId from 'resources/intl';
import { AddCircle as AddCircleIcon, Search as SearchIcon } from '@mui/icons-material';
import colors from 'resources/colors';
import { NormalButton } from 'components/NormalButton';
import { DataGrid, Column, FilterRow, Scrolling, Selection } from 'devextreme-react/data-grid';
import dimens from 'resources/dimens';
import { DxDataGridColumn } from 'components/DxDataGrid';
import constants from 'resources/constants';
import { AdminAccount } from 'models/accounts';
import { AccountEditDialog } from './AccountEditDialog';
import config from 'resources/config';
import dayjs from 'dayjs';
import { useGeneralSettingsEditAccountResult } from 'features/contents/generalSettings/generalSettingsSlice';
import { getTodayLimit } from 'utils/misc';

interface AccountManagementProps {
  open: boolean;
  height: number;
}

export function AccountManagement(props: AccountManagementProps): JSX.Element {
  const { open, height } = props;
  const intl = useIntl();
  const dispatch = useDispatch();
  const accounts = useAdminSettingsAccounts();
  const accountResult = useAdminSettingsEditAccountResult();
  const accountGroupResult = useAdminSettingsEditAccountGroupResult();
  const generalAccountResult = useGeneralSettingsEditAccountResult();
  const dataGridRef = useRef<DataGrid<AdminAccount, unknown>>(null);
  const minDate = dayjs(config.minDate).toDate();
  const maxDate = dayjs(getTodayLimit()).toDate();
  const columns: DxDataGridColumn[] = [
    {
      key: 'cognitoAccountId',
      dataField: 'cognitoAccountId',
      caption: intl.formatMessage({ id: msgId.accountName }),
      width: 150,
      defaultSortOrder: 'asc',
    },
    {
      key: 'mailAddress',
      dataField: 'mailAddress',
      caption: intl.formatMessage({ id: msgId.mailAddress }),
    },
    {
      key: 'accountGroupName',
      dataField: 'accountGroupName',
      caption: intl.formatMessage({ id: msgId.accountGroup }),
      width: 200,
    },
    {
      key: 'accountStatus',
      dataField: 'accountStatus',
      caption: intl.formatMessage({ id: msgId.accountStatus }),
      width: 200,
    },
    {
      key: 'createdAt',
      dataField: 'createdAt',
      dataType: 'datetime',
      filterOperations: constants.dataGrid.dateFilterOperations,
      format: constants.dateFormat.yyyyMMddHHmm,
      caption: intl.formatMessage({ id: msgId.createdAt }),
      width: 160,
      editorOptions: { min: minDate, max: maxDate },
    },
    {
      key: 'updatedAt',
      dataField: 'updatedAt',
      dataType: 'datetime',
      filterOperations: constants.dataGrid.dateFilterOperations,
      format: constants.dateFormat.yyyyMMddHHmm,
      caption: intl.formatMessage({ id: msgId.updatedAt }),
      width: 160,
      editorOptions: { min: minDate, max: maxDate },
    },
  ];

  /**
   * 行選択
   * @param account アカウント
   */
  const handleSelectionChanged = (account: AdminAccount) => {
    dataGridRef.current?.instance.clearSelection();
    if (account != null) {
      dispatch(setAccountEditing({ editing: true, account: account }));
    }
  };

  /**
   * 新規登録
   */
  const handleRegistrationClick = () => {
    dispatch(setAccountEditing({ editing: true, account: undefined }));
  };

  /**
   * フィルターリセット
   */
  const handleFilterResetClick = () => {
    if (dataGridRef.current) {
      const dataGrid = dataGridRef.current.instance;
      columns.forEach((c) => {
        dataGrid.columnOption(c.dataField, 'selectedFilterOperation', null);
      });
      dataGrid.clearFilter();
    }
  };

  // 日付入力のフィルターをreadonlyにする。
  const setInputReadOnly = () => {
    const element = document.getElementById('accountDataGrid');
    if (element) {
      const elements = [5, 6];
      elements.forEach((colIndex) => {
        const dateFilterInput = element.querySelector(
          '.dx-datagrid-filter-row td[aria-colindex="' + colIndex + '"]'
        ) as HTMLElement;
        if (dateFilterInput) {
          const inputElement = dateFilterInput.querySelector(
            '.dx-texteditor-input'
          ) as HTMLInputElement;
          if (inputElement) {
            inputElement.readOnly = true;
          }
        }
      });
    }
  };

  /**
   * API完了後Account情報を再取得
   */
  useEffect(() => {
    if (open && accountResult && accountResult.isSuccess) {
      dispatch(fetchAccountsSilent());
    }
  }, [dispatch, open, accountResult]);

  useEffect(() => {
    if (accountGroupResult && accountGroupResult.isSuccess) {
      dispatch(fetchAccountsSilent());
    }
  }, [dispatch, accountGroupResult]);

  useEffect(() => {
    if (generalAccountResult && generalAccountResult.isSuccess) {
      dispatch(fetchAccountsSilent());
    }
  }, [dispatch, generalAccountResult]);

  return (
    <Box
      sx={{
        position: 'absolute',
        width: '100%',
        background: colors.subDrawer.background,
        visibility: open ? 'visible' : 'hidden',
      }}
    >
      {accounts && (
        <React.Fragment>
          <ContentsHeader title={intl.formatMessage({ id: msgId.accountManagement })}>
            <NormalButton
              label={intl.formatMessage({ id: msgId.filterReset })}
              color="primary"
              startIcon={<SearchIcon />}
              onClick={() => handleFilterResetClick()}
            />
            <NormalButton
              label={intl.formatMessage({ id: msgId.newRegistration })}
              color="primary"
              startIcon={<AddCircleIcon />}
              onClick={() => handleRegistrationClick()}
            />
          </ContentsHeader>
          <DataGrid
            id="accountDataGrid"
            style={{ margin: dimens.dataGrid.margin }}
            ref={dataGridRef}
            height={height - dimens.dataGrid.offsetHeight}
            dataSource={accounts}
            showBorders={true}
            allowColumnResizing={true}
            hoverStateEnabled={true}
            columnResizingMode="nextColumn"
            keyExpr="cognitoAccountId"
            columnAutoWidth={true}
            noDataText={intl.formatMessage({ id: msgId.noResults })}
            onSelectionChanged={(e) => handleSelectionChanged(e.selectedRowsData[0])}
            onCellClick={setInputReadOnly}
          >
            <Selection mode="single" />
            <FilterRow visible={true} />
            <Scrolling mode="virtual" />
            {columns.map((column) => {
              return (
                <Column
                  key={column.key}
                  name={column.key}
                  dataField={column.dataField}
                  minWidth={dimens.dataGrid.column.minWidth}
                  dataType={column.dataType}
                  format={column.format}
                  caption={column.caption}
                  width={column.width}
                  filterOperations={column.filterOperations}
                  defaultSortOrder={column.defaultSortOrder}
                  editorOptions={column.editorOptions}
                />
              );
            })}
          </DataGrid>
        </React.Fragment>
      )}
      <AccountEditDialog contentHeight={height} />
    </Box>
  );
}
